import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalBody,
  Button,
  useDisclosure,
  HStack,
  IconCreditCard,
  Text,
  IconArrow,
  IconClose,
  Divider,
  IconPaymentBoleto,
  IconPix,
} from '@gazin/ecommerce-ui'
import { Fragment } from 'react'
import { PaymentMethodsEnum } from 'src/util/enums/payment-methods'
import {
  AvailablePaymentMethodProps,
  PaymentMethodsInformationProps,
} from './types'
import { usePaymentMethod } from 'src/contexto/hooks/usePaymentMethod'
import { PaymentMethodInfo } from './paymentMethodInfo'

export const PaymentMethodsInformationModal = ({
  installments,
  installmentsMaxQuantityWithoutInterest,
  totalInCash,
  totalInInstallments,
}: PaymentMethodsInformationProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { payment } = usePaymentMethod()
  const activePaymentMethodsStore = Object.values(payment).filter(
    (item) => item
  )

  const renderPaymentMethodsAvailable = (
    paymentMethodAvailable: PaymentMethodsEnum
  ): AvailablePaymentMethodProps => {
    switch (paymentMethodAvailable) {
      case PaymentMethodsEnum.Credit_card:
        return {
          slug: PaymentMethodsEnum.Credit_card,
          icon: (
            <IconCreditCard
              height="1.5rem"
              width="1.5rem"
              variant="blue"
              color="primary.main"
            />
          ),
          name: 'Cartão de crédito',
          installments,
          installmentsMaxQuantityWithoutInterest,
          totalInInstallments: totalInInstallments,
          totalInCash: totalInCash,
        }
      case PaymentMethodsEnum.Billet:
        return {
          slug: PaymentMethodsEnum.Billet,
          icon: (
            <IconPaymentBoleto
              height="1.5rem"
              width="1.5rem"
              color="primary.main"
            />
          ),
          name: 'Boleto',
          description:
            'O boleto será gerado após a finalização de sua compra. Imprima e pague no banco ou pague pela internet utilizando o código de barras do boleto.',
          totalInCash: totalInCash,
          totalInInstallments: totalInInstallments,
        }
      case PaymentMethodsEnum.Pix:
        return {
          slug: PaymentMethodsEnum.Pix,
          icon: <IconPix height="1.5rem" width="1.5rem" color="primary.main" />,
          name: 'Pix',
          description:
            'Pague utilizando o QR Code ou Copia e Cola, com aprovação imediata. Na etapa de finalização da compra, a gente explica direitinho como pagar com Pix.',
          totalInInstallments: totalInInstallments,
          totalInCash: totalInCash,
        }
    }
  }

  return (
    <>
      <Button
        w="100%"
        h="2.375rem"
        border="0.0625rem solid #A0A4B3"
        bg="transparent"
        _hover={{ bg: 'transparent' }}
        mt={3}
        onClick={onOpen}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        data-testid="button-payment-information"
      >
        <HStack
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
          gap="0"
          fontSize="0.75rem"
          lineHeight="0.9rem"
          color="primary.500"
        >
          <IconCreditCard width="1rem" height="1rem" variant="blue" />
          <Text>Mais formas de pagamento</Text>
        </HStack>
        <IconArrow color="primary.500" height=".65rem" width=".65rem" />
      </Button>

      <Modal onClose={onClose} isOpen={isOpen} size={{ base: 'xs', md: 'md' }}>
        <ModalOverlay />
        <ModalContent
          p={{ base: '1rem 1.125rem', md: '1.5rem 2rem' }}
          gap={{ base: '1rem', md: '2rem' }}
        >
          <ModalHeader
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            fontWeight={600}
            fontSize={{ base: '1.125rem', md: '1.5rem' }}
            lineHeight={{ base: '1.575rem', md: '2.1rem' }}
            color="neutral.800"
            p={0}
          >
            <Text>Formas de pagamento</Text>
            <IconClose
              cursor="pointer"
              color="neutral.500"
              width="0.625rem"
              height="0.625rem"
              onClick={onClose}
            />
          </ModalHeader>
          <ModalBody
            w="100%"
            p={0}
            display="flex"
            flexDir="column"
            gap={{ base: '0.5rem', md: '1rem' }}
            overflow="auto"
            scrollBehavior="smooth"
            css={{
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {activePaymentMethodsStore.map((paymentMethodAvailable, index) => {
              const isLast = index === activePaymentMethodsStore.length - 1
              return (
                <Fragment key={paymentMethodAvailable?.slug}>
                  <PaymentMethodInfo
                    {...renderPaymentMethodsAvailable(
                      paymentMethodAvailable?.slug as PaymentMethodsEnum
                    )}
                  />
                  {!isLast && <Divider color="#E3E3E8" />}
                </Fragment>
              )
            })}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
